import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DEBOUNCE_DELAY } from 'utils/constants/app/magic-numbers-app';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { TOUR_STEPS_TYPES } from 'utils/constants/tour';
import {
  detectTourMethod,
  TOUR_PATH_NAMESPACE
} from 'utils/generic/tour-settings';
import { isMobile } from 'utils/is-mobile';
import { TTourSteps } from 'interfaces/tour';
import { useDebounce } from 'hooks/useDebounce';
import { setTourSteps } from 'store/actions';
import {
  getTourModalVisible,
  getTourSteps,
  gettourStepsInitialData
} from 'store/selectors/app-data';
import { getIsLoggedIn, getUser } from 'store/selectors/user-data';

type TUseTourProps = {
  tourSteps: TTourSteps;
  tourWrapperActive: boolean;
};

export function useTour(): TUseTourProps {
  const [tourMethods, setTourMethods] = useState<TOUR_STEPS_TYPES[]>([]);

  const user = useSelector(getUser);
  const tourSteps = useSelector(getTourSteps);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const tourModalVisible = useSelector(getTourModalVisible);
  const tourStepsInitialData = useSelector(gettourStepsInitialData);

  const dispatch = useDispatch();
  const resource = TOUR_PATH_NAMESPACE();

  const tourWrapperActive =
    isLoggedIn &&
    !!resource &&
    !isMobile() &&
    !user.pending &&
    Boolean(Object.values(tourSteps || {}).find(step => step.active));

  const isActiveTour =
    resource &&
    isLoggedIn &&
    !isMobile() &&
    !user.pending &&
    SpringConfigs.USER_PERSONALIZATION_ENABLED &&
    Object.keys(tourStepsInitialData[resource] || {}).length;

  const resources = useMemo((): TTourSteps => {
    if (!resource || !tourMethods.length) {
      return {};
    }

    let currentActiveTourStep = -1;

    if (
      !tourStepsInitialData?.[resource]?.finished &&
      !tourStepsInitialData?.[resource]?.skipped
    ) {
      currentActiveTourStep = Number(
        Object.keys(tourStepsInitialData[resource] || {}).find(
          step => +step >= 0
        )
      );
    }

    return tourMethods.reduce<TTourSteps>(
      (list, selector, index) => ({
        ...list,
        [index]: detectTourMethod(selector, index, currentActiveTourStep)
      }),
      {}
    );
  }, [tourStepsInitialData, resource, tourMethods]);

  useEffect(() => {
    if (isActiveTour) {
      dispatch(setTourSteps(resources || {}));
    }

    return () => {
      dispatch(setTourSteps({}));
    };
  }, [isLoggedIn, user.pending, resources]);

  useDebounce(
    () => {
      if (isActiveTour && tourModalVisible) {
        let tours: TOUR_STEPS_TYPES[] = [];

        document.querySelectorAll('[data-tour]').forEach(tour => {
          const tourStepType = tour.getAttribute(
            'data-tour'
          ) as TOUR_STEPS_TYPES | null;

          tourStepType && tours.push(tourStepType);
        });

        tours = [...new Set(tours)];

        if (tours.includes(TOUR_STEPS_TYPES.addCompetitions)) {
          tours = tours.filter(
            tour => tour !== TOUR_STEPS_TYPES.addCompetitionsTopMatches
          );
        }

        setTourMethods(tours);
      }
    },
    DEBOUNCE_DELAY._3000,
    [isActiveTour, tourModalVisible]
  );

  return {
    tourSteps,
    tourWrapperActive
  };
}
